import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { FaCreditCard, FaUniversity, FaPaypal } from "react-icons/fa";
import "../styles/Payment.css";
import CONFIG from "../utils/config";

const PaymentInfo = () => {
  const [paymentMethod, setPaymentMethod] = useState("");
  const [bank, setBank] = useState('');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: "",
    expiry: "",
    cvv: "",
    cardHolderName: "",
  });
  const [qpay, setQpay] = useState('');
  const [loading, setLoading] = useState(true);
  const [policyData, setPolicyData] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  useEffect(() => {
    const fetchPolicyDetails = async () => {
      if (!id) {
        toast.error("Policy ID not found. Redirecting to home.", { position: "top-right", autoClose: 2000 });
        navigate("/");
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${CONFIG.BASE}/insurance/getsgs`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: id }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch policy details.");
        }

        const data = await response.json();
        setPolicyData(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching policy details:", error);
        toast.error("Failed to load policy details. " + error.message, { position: "top-right", autoClose: 2000 });
        setLoading(false);
        navigate("/");
      }
    };

    fetchPolicyDetails();
  }, [id, navigate]);

  const premium = policyData?.premiumAmount;
  const insured = policyData?.insuredName;
  const currency = policyData?.currency;
  const registrationNumber = policyData?.vehicleRegistrationNumber;
  const chassisNumber = policyData?.chassisNumber;
  const makeModel = policyData?.makeModel;
  const manufacturingYear = policyData?.manufacturingYear;
  const expiryDate = policyData?.insuranceExpiryDate;
  const vehicleValue = policyData?.vehicleValue;
  const policyNumber = policyData?.policyNumber;
  const policyStartDate = policyData?.policyStartDate;
  const policyExpiryDate = policyData?.policyExpiryDate;
  const coverageType = policyData?.policyCovers ? policyData.policyCovers.split(",") : [];
  const plan = policyData?.existingPlan;
  const policyCovers = policyData?.policyCovers;
  const mobileNumber = policyData?.mobileNumber;
  
  const handlePaymentChange = (method) => {
    setPaymentMethod(method);
  };

  const formatCardNumber = (cardNumber) => {
    return cardNumber.replace(/\d{4}(?=\d)/g, "$& ").trim();
  };

  const detectCardType = (cardNumber) => {
    const cleanedCardNumber = cardNumber.replace(/\D/g, '');
    const regexVisa = /^4/;
    const regexMasterCard = /^5[1-5]/;
    const regexAmex = /^3[47]/;
    const regexDiscover = /^6/;
    if (regexVisa.test(cleanedCardNumber)) {
      return "Visa";
    } else if (regexMasterCard.test(cleanedCardNumber)) {
      return "MasterCard";
    } else if (regexAmex.test(cleanedCardNumber)) {
      return "American Express";
    } else if (regexDiscover.test(cleanedCardNumber)) {
      return "Discover";
    }
    return "Unknown";
  };

  const getCardBackground = (cardNumber) => {
    const cardType = detectCardType(cardNumber);
    const cardDetailsMap = {
      Visa: { backgroundImage: './images/6.jpeg', logo: './images/visa.png' },
      MasterCard: { backgroundImage: './images/18.jpeg', logo: './images/mastercard.png' },
      AmericanExpress: { backgroundImage: './images/3.jpeg', logo: './images/amex.png' },
      Discover: { backgroundImage: './images/4.jpeg', logo: './images/unionpay.png' },
      default: { backgroundImage: './images/17.jpeg', logo: './images/default.png' },
    };
    return cardDetailsMap[cardType] || cardDetailsMap['default'];
  };

  const handleCardDetailsChange = (field, value) => {
    if (field === "expiry") {
      value = value.replace(/\D/g, '');
      if (value.length >= 3) {
        value = `${value.slice(0, 2)}/${value.slice(2, 4)}`;
      }
      if (value.length > 5) {
        value = value.slice(0, 5);
      }
    }
    setCardDetails({ ...cardDetails, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!paymentMethod) {
      toast.error("Please select a payment method!", { position: "top-right", autoClose: 2000 });
      return;
    }

    if (paymentMethod === "Credit/Debit Card" && (!cardDetails.cardNumber || !cardDetails.expiry || !cardDetails.cvv || !cardDetails.cardHolderName)) {
      toast.error("Please enter complete card details!", { position: "top-right", autoClose: 2000 });
      return;
    }

    if (paymentMethod === "QPay" && !qpay) {
      toast.error("Please enter your QPay account!", { position: "top-right", autoClose: 2000 });
      return;
    }

    toast.success("Payment Successful! Sending quotation email...", { position: "top-right", autoClose: 2000 });

    try {
      const emailData = {
        clientName: policyData.insuredName,
        make: policyData.makeModel,
       
        year: policyData.manufacturingYear,
        premium: `${policyData.currency} ${new Intl.NumberFormat().format(policyData.premiumAmount)}`,
        recommendedOption: policyData.newPlan,
        // recipientEmail: policyData.email,
        recipientEmail: "gopinath@insursa.com",
        senderName: policyData.senderName,
        senderTitle: policyData.senderTitle,
        senderPhone: policyData.senderPhone,
        senderEmail: policyData.senderEmail,
        companyName: policyData.companyName,
        website: policyData.website,
      };

      // Create a Blob with the JSON data and a specific content type
      const dataBlob = new Blob([JSON.stringify(emailData)], { type: 'application/json' });
      
      const formData = new FormData();
      formData.append('data', dataBlob);

      const emailResponse = await fetch('http://digital.qgirco.com:7778 /api/email/send-quotation', {
        method: 'POST',
        body: formData,
      });

      if (!emailResponse.ok) {
        throw new Error("Failed to send quotation email.");
      }

      toast.success("Quotation email sent successfully!", { position: "top-right", autoClose: 2000 });
    } catch (error) {
      console.error("Email API Error:", error);
      toast.error("Failed to send quotation email. Please try again.", { position: "top-right", autoClose: 2000 });
    }

    setTimeout(() => {
      navigate("/approval", {
        state: {
          premium,
          insured,
          currency,
          registrationNumber,
          chassisNumber,
          makeModel,
          manufacturingYear,
          expiryDate,
          vehicleValue,
          policyNumber,
          policyStartDate,
          policyExpiryDate,
          coverageType,
          plan,
          policyCovers,
          mobileNumber,
          id,
        }
      });
    }, 2000);
  };

  if (loading) {
    return (
      <div className="payment-info__container">
        <div className="payment-info__box">
          <h3 className="payment-info__title">Loading Policy Details...</h3>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!policyData) {
    return (
      <div className="payment-info__container">
        <div className="payment-info__box">
          <h3 className="payment-info__title">Error loading policy details.</h3>
          <p>Please go back and try again.</p>
          <button onClick={() => navigate("/")} className="payment-info__btn-primary">Go to Home</button>
        </div>
      </div>
    );
  }

  const { backgroundImage, logo } = getCardBackground(cardDetails.cardNumber);
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat().format(amount);
  };

  return (
    <div className="payment-info__container">
      <div className="payment-info__box">
        <h3 className="payment-info__title">💳 Select Payment Method</h3>
        <div className="payment-info__selected-premium">
          <h6>Insured Name: <strong>{insured}</strong></h6>
          <h6>💰 Premium Amount: <strong>{formatCurrency(premium)} <span className="text-primary">{currency}</span></strong></h6>
        </div>
        <div className="payment-info__options">
          <button
            className={`payment-info__btn ${paymentMethod === "Credit/Debit Card" ? "selected" : ""}`}
            onClick={() => handlePaymentChange("Credit/Debit Card")}
          >
            <FaCreditCard /> Credit/Debit Card
          </button>
          <button
            className={`payment-info__btn ${paymentMethod === "Net Banking" ? "selected" : ""}`}
            onClick={() => handlePaymentChange("Net Banking")}
          >
            <FaUniversity /> Net Banking
          </button>
          <button
            className={`payment-info__btn ${paymentMethod === "QPay" ? "selected" : ""}`}
            onClick={() => handlePaymentChange("QPay")}
          >
            <FaPaypal /> QPay
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          {paymentMethod === "Credit/Debit Card" && (
            <div className="payment-info__card-cover" style={{ backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover' }}>
              <div className="payment-info__card-front">
                <div className="payment-info__chip">
                  <img src='./images/chip.png' alt="Chip" className="payment-info__chip-image" />
                </div>
                <div className="payment-info__card-holder-name">
                  <input
                    type="text"
                    placeholder="CARDHOLDER NAME"
                    value={cardDetails.cardHolderName}
                    onChange={(e) => handleCardDetailsChange("cardHolderName", e.target.value)}
                    maxLength="50"
                    className="payment-info__input text-uppercase"
                  />
                </div>
                <div className="payment-info__card-number">
                  <input
                    type="text"
                    placeholder="XXXX XXXX XXXX XXXX"
                    value={formatCardNumber(cardDetails.cardNumber)}
                    onChange={(e) => handleCardDetailsChange("cardNumber", e.target.value.replace(/\D/g, ""))}
                    maxLength="19"
                    className={`payment-info__input ${detectCardType(cardDetails.cardNumber)}`}
                  />
                </div>
                <div className="payment-info__card-logo">
                  <img src={logo} alt="Card Logo" className="payment-info__logo-image" />
                </div>
                <div className="payment-info__expiry-cvv">
                  <input
                    type="text"
                    placeholder="MM/YY"
                    value={cardDetails.expiry}
                    onChange={(e) => handleCardDetailsChange("expiry", e.target.value)}
                    maxLength="5"
                    className="payment-info__input"
                  />
                  <input
                    type="password"
                    placeholder="CVV"
                    value={cardDetails.cvv}
                    onChange={(e) => handleCardDetailsChange("cvv", e.target.value)}
                    maxLength="3"
                    className="payment-info__input"
                  />
                </div>
              </div>
            </div>
          )}
          {paymentMethod === "Net Banking" && (
            <div className="payment-info__form">
              <select
                className="form-select"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
              >
                <option value="">Select Your Bank</option>
                <option value="HDFC">HDFC Bank</option>
                <option value="ICICI">ICICI Bank</option>
                <option value="SBI">State Bank of India</option>
                <option value="Axis">Axis Bank</option>
                <option value="Kotak">Kotak Mahindra Bank</option>
              </select>
            </div>
          )}
          {paymentMethod === "QPay" && (
            <div className="payment-info__form">
              <input
                type="text"
                placeholder="Enter QPay Account"
                value={qpay}
                onChange={(e) => setQpay(e.target.value)}
                className="form-control"
              />
            </div>
          )}
          <button type="submit" className="payment-info__btn-primary fw-normal">
            Submit Payment
          </button>
        </form>
      </div>
    </div>
  );
};

export default PaymentInfo;